import { useEffect, useMemo, useRef, useState } from 'react';
import { useListOfObjects } from '.';
import { useCustomToast } from './use-suigar-toast';
import { Transaction } from '@mysten/sui/transactions';
import { APP_CFG } from '@/configs';
import { LootboxEvent } from '@/dtos';
import { useAuth } from '@/hooks/useAuth';
import { usePendingReveal } from '@/hooks/usePendingReveal';
import { useLoginModalStore } from '@/stores/login-modal-store';
import { useDepositModalStore } from '@/stores/deposit-modal-store';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrencyStore } from '@/stores/currency-store';
import { useCurrency } from '@/hooks/useCurrency';
import { useConfetti } from '@/hooks/useConfetti';
import { calculateConfettiBurst } from '@/utils/confetti';
import { gsap } from 'gsap';
import { createSound, sharedWinSoundsTiered } from '@/utils/sound';
import { positiveExpressions, negativeExpressions } from '@/components/suifrens/expressions';
import { CapyExpression } from '@suigar/suifrens-display-system';
import { LootboxType, lootbox_50_SUI, lootbox_5_SUI, lootboxes, Candy } from '@/pages/lootbox/assets';
import { playResultSound } from '@/utils/playResultSound';
import { useGameTransactionController } from './useGameTransactionController';
import { useTopGameActivities } from './useTopGameActivities';
import { normalizeSuiErrorMessage, isSignatureInvalidError } from '@/utils/errors';
import { filterPendingActivities } from '@/utils/activity';
import { runStandardGameRound } from './runStandardGameRound';
import { createSeededRng } from '@/utils/seededRandom';
import { winIntensityFromMultiplier } from '@/utils/winIntensity';
import lootboxLessSoundSrc from '@/assets/sounds/lootbox_less.mp3';
import rollSoundSrc from '@/assets/sounds/roll.wav';

// Define sound effects
const rollSound = createSound(rollSoundSrc, 0.01);
const winSounds = sharedWinSoundsTiered;
const loseSounds = [createSound(lootboxLessSoundSrc, 0.04)];

function getRandomExpression(isPositive: boolean): CapyExpression {
	return isPositive
		? positiveExpressions[Math.floor(Math.random() * positiveExpressions.length)]
		: negativeExpressions[Math.floor(Math.random() * negativeExpressions.length)];
}

function selectTopThreePrize(lootbox: LootboxType): Candy {
	const topThreeCandies = lootbox.candies
		.slice()
		.sort((a: Candy, b: Candy) => b.prize - a.prize)
		.slice(0, 3);
	return topThreeCandies[Math.floor(Math.random() * topThreeCandies.length)];
}

function selectRandomCandy(lootbox: LootboxType): Candy {
	const rand = Math.random();
	let cumulativeProba = 0;
	for (let i = 0; i < lootbox.candies.length; i++) {
		cumulativeProba += lootbox.visu_probas[i];
		if (rand < cumulativeProba) {
			return lootbox.candies[i];
		}
	}
	return lootbox.candies[lootbox.candies.length - 1];
}

function getIntersectionArea(rect1: any, rect2: any): number {
	const xOverlap = Math.max(0, Math.min(rect1.right, rect2.right) - Math.max(rect1.left, rect2.left));
	const yOverlap = Math.max(0, Math.min(rect1.bottom, rect2.bottom) - Math.max(rect1.top, rect2.top));
	return xOverlap * yOverlap;
}

export function useLootboxPage() {
	const toast = useCustomToast();
	const { selectedCurrency, fromBaseUnits, toBaseUnits, formatStandardAmount } = useCurrency();

	// Game state
	const [isOpen, setIsOpen] = useState(false);
	const [isAnimating, setIsAnimating] = useState(false);
	const [selectedLootbox, setSelectedLootbox] = useState<LootboxType>();
	const [lootbox, setLootbox] = useState(lootbox_50_SUI);
	const [topFadeOpacity, setTopFadeOpacity] = useState(0);
	const [bottomFadeOpacity, setBottomFadeOpacity] = useState(0);
	const [capyExpression, setCapyExpression] = useState<CapyExpression>('happy');
	const [currentLootboxEvent, setCurrentLootboxEvent] = useState<any>(null);
	const [currentBalanceBetId, setCurrentBalanceBetId] = useState<string | null>(null);
	const [isTransactionPending, setIsTransactionPending] = useState(false);
	const { pendingIdsFor, finalize } = usePendingReveal();

	// Refs
	const containerRef = useRef<any>(null);
	const bigRectangleRef = useRef<any>(null);
	const slowRotateTween = useRef<any>(null);
	const scrollRef = useRef<any>(null);
	const idleResumeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
	const wasAnimatingRef = useRef(false);

	// Hooks
	const { lootboxes: suigar, lootboxesIsLoading: suigarIsLoading, lootboxActivities, lootboxActivitiesLoading } = useListOfObjects();
	const { openLoginModal } = useLoginModalStore();
	const { openDepositModal } = useDepositModalStore();
	const { address, displayedBalance, disconnect, currentAccount } = useAuth();
	const [confetti, triggerConfetti] = useConfetti();
	const { runGamePlay: runLootboxGame } = useGameTransactionController('lootbox', {
		setTransactionPending: setIsTransactionPending,
		onRequireLogin: openLoginModal,
	});

	const queryClient = useQueryClient();
	const { data: topLootboxActivities, isLoading: topLootboxActivitiesLoading } = useTopGameActivities('lootbox', { limit: 10 });

	const { selectedCurrency: currencyStore } = useCurrencyStore();

	const createPlayTransaction = (input: { id: string; price: number }, gambler_address: string) => {
		const txb = new Transaction();

		const coin = txb.splitCoins(txb.gas, [input.price]);
		txb.moveCall({
			arguments: [
				txb.object(APP_CFG.LOOTBOX_LOOTBOXGAME_SUI_SUI),
				txb.object(APP_CFG.HOUSE_HOUSE_SUI_SUI),
				txb.pure.address(input.id),
				coin,
				txb.object('0x08'),
			],
			target: `${APP_CFG.PACKAGE_2}::lootbox::place_bet_and_reveal_lootbox_onchain_randomness`,
			typeArguments: [currencyStore.type],
		});

		txb.transferObjects([coin], gambler_address);

		return txb;
	};

	const parseResponse = (response: any) => {
		const purchasedLootboxEvent = (response as any).events?.pop() as LootboxEvent;
		if (!purchasedLootboxEvent || (response as any).effects.status.status == 'failure') {
			const rawError = (response as any).effects.status.error;
			if (isSignatureInvalidError(rawError)) {
				disconnect();
				openLoginModal({ sessionExpired: true });
			}

			const error = normalizeSuiErrorMessage(rawError, { currencySymbol: currencyStore.symbol });
			toast({ title: 'Transaction failed', description: error, status: 'error' });
			throw Error(error);
		}

		// Refresh quests after successful game
		if (currentAccount?.address) {
			queryClient.invalidateQueries({
				queryKey: ['questStatuses', 'lootbox', currentAccount.address],
			});
		}

		return purchasedLootboxEvent;
	};

	// Animation and game logic functions
	function handleScroll() {
		if (scrollRef.current) {
			const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
			const maxScrollTop = scrollHeight - clientHeight;

			const topOpacity = Math.min(scrollTop / 20, 1);
			setTopFadeOpacity(topOpacity);

			const bottomOpacity = Math.min((maxScrollTop - scrollTop) / 20, 1);
			setBottomFadeOpacity(bottomOpacity);
		}
	}

	function initializeRectangles() {
		if (!containerRef.current) return;

		const container = containerRef.current;
		const existingRectangles = container.querySelectorAll('.rectangle');
		if (existingRectangles.length > 0) return;

		const numberOfRectangles = 24;
		for (let i = 0; i < numberOfRectangles; i++) {
			const rect = document.createElement('div');
			rect.classList.add('rectangle');
			rect.classList.add('opacity-0');
			rect.classList.add(
				'max-md:w-[calc(var(--rectangle-width)/1.3)]',
				'max-md:h-[calc(var(--rectangle-height)/1.3)]',
				'w-(--rectangle-width)',
				'h-(--rectangle-height)',
			);
			const img = document.createElement('img');
			img.alt = 'Candy';
			rect.appendChild(img);
			const reward = document.createElement('div');
			reward.classList.add('reward-text');
			rect.appendChild(reward);
			container.appendChild(rect);
		}
		const rectangles = container.querySelectorAll('.rectangle');
		setTimeout(() => {
			gsap.to(rectangles, {
				scale: 1,
				opacity: 1,
				duration: 0.7,
				ease: 'power1.out',
				stagger: 0.02,
			});
		}, 20);
		const angleStep = 360 / rectangles.length;
		rectangles.forEach((rect: any, index: number) => {
			const angle = index * angleStep + 180;
			rect.style.transform = `rotate(${angle}deg)`;
		});
	}

	function updateRectangles(lootbox: LootboxType) {
		if (!containerRef.current) return;

		const rectangles = containerRef.current.querySelectorAll('.rectangle');
		rectangles.forEach((rect: any) => {
			const candy = selectRandomCandy(lootbox);
			rect.querySelector('img').src = candy.candy_image;
			rect.querySelector('div').textContent = formatStandardAmount(candy.prize);
		});
		updateBigRectangleContent();
	}

	function updateBigRectangleContent() {
		if (!containerRef.current || !bigRectangleRef.current) return;

		const rectangles = containerRef.current.querySelectorAll('.rectangle');
		const bigRectBounding = bigRectangleRef.current.getBoundingClientRect();
		let maxIntersectionArea = 0;
		let bestMatchRect: any = null;
		rectangles.forEach((rect: any) => {
			const rectBounding = rect.getBoundingClientRect();
			const intersectionArea = getIntersectionArea(rectBounding, bigRectBounding);
			if (intersectionArea > maxIntersectionArea) {
				maxIntersectionArea = intersectionArea;
				bestMatchRect = rect;
			}
		});
		if (bestMatchRect) {
			const bigRewardText = bigRectangleRef.current.querySelector('.big-reward-text');
			const bigImg = bigRectangleRef.current.querySelector('img');
			if (bigRewardText) bigRewardText.textContent = bestMatchRect.querySelector('div').textContent;
			if (bigImg) bigImg.src = bestMatchRect.querySelector('img').src;
		}
	}

	function slowRotate() {
		if (!containerRef.current) return;

		const rectangles = containerRef.current.querySelectorAll('.rectangle');
		slowRotateTween.current = gsap.to(rectangles, {
			rotation: '+=360',
			duration: 250,
			ease: 'linear',
			repeat: -1,
			onUpdate: updateBigRectangleContent,
		});
	}

	function startRotation() {
		if (isAnimating || !containerRef.current) return;
		setIsAnimating(true);
		setCapyExpression('shocked');
		gsap.killTweensOf('.rectangle');
		const rectangles = containerRef.current?.querySelectorAll('.rectangle');
		const spinSeed = String(currentLootboxEvent?.parsedJson?.bet_id ?? Date.now());
		const rng = createSeededRng(spinSeed);
		const extraTurns = 2 + Math.floor(rng() * 3); // 2–4 full turns after alignment
		const duration = 3.2 + rng() * 1.3; // 3.2s–4.5s
		const highestRewardCandy = lootbox.candies.reduce((maxCandy, candy) => (candy.prize > maxCandy.prize ? candy : maxCandy), lootbox.candies[0]);
		const currentRotation = gsap.getProperty(rectangles[0], 'rotation') as any;
		const additionalRotation = ((360 - currentRotation) % 360) + extraTurns * 360;

		const nearWin = rng() < 0.32;
		const nearWinDirection = rng() < 0.5 ? -1 : 1;

		let lastRotation = currentRotation;

		rectangles.forEach((rect: any, index: number) => {
			gsap.to(rect, {
				rotation: `+=${additionalRotation}`,
				duration: duration,
				ease: 'power2.out',
				onUpdate: () => {
					const currentRotation = gsap.getProperty(rect, 'rotation') as any;
					const rotationDifference = Math.abs(currentRotation - lastRotation);

					if (rotationDifference >= 15 && index === 0) {
						rollSound.volume(0.1 + Math.random() * 0.05); // Set random volume between 0.10 and 0.15
						rollSound.play();
						lastRotation = currentRotation;
					}

					if (currentRotation % 360 >= 170 && currentRotation % 360 <= 190) {
						const reward = rect.querySelector('div');
						const randomCandy = selectRandomCandy(lootbox);
						rect.querySelector('img').src = randomCandy.candy_image;
						reward.textContent = `${formatStandardAmount(randomCandy.prize)}`;
						if (index == 0) {
							const rewardAmount = currentLootboxEvent ? fromBaseUnits(currentLootboxEvent.parsedJson.reward) : highestRewardCandy.prize;
							reward.textContent = formatStandardAmount(rewardAmount);
							rect.querySelector('img').src = currentLootboxEvent
								? lootbox.candies.find(
										(candy) =>
											candy.prize == fromBaseUnits(currentLootboxEvent.parsedJson.reward) ||
											(APP_CFG.NETWORK == 'testnet' && candy.prize == fromBaseUnits(currentLootboxEvent.parsedJson.reward) * 1000),
									)?.candy_image
								: highestRewardCandy.candy_image;
						} else if (
							nearWin &&
							((nearWinDirection === -1 && rect === rectangles[rectangles.length - 1]) || (nearWinDirection === 1 && rect === rectangles[1]))
						) {
							const nearWinPrize = selectTopThreePrize(lootbox);
							rect.querySelector('img').src = nearWinPrize.candy_image;
							reward.textContent = formatStandardAmount(nearWinPrize.prize);
						}
					}
					updateBigRectangleContent();
				},
				onComplete:
					index === 0
						? () => {
								setCapyExpression(getRandomExpression((currentLootboxEvent?.parsedJson.reward ?? 0) > 0));
								if (bigRectangleRef.current) {
									gsap.to(bigRectangleRef.current, {
										scale: 1.1,
										duration: 0.5,
										ease: 'power2.out',
										stagger: 0.05,
										onComplete: () => {
											if (bigRectangleRef.current) {
												gsap.to(bigRectangleRef.current, {
													scale: 1,
													duration: 0.5,
													ease: 'power2.out',
													stagger: 0.05,
												});
											}
										},
									});
								}
								if (currentBalanceBetId) {
									finalize({ balanceBetId: currentBalanceBetId, betId: currentLootboxEvent?.parsedJson.bet_id });
									setCurrentBalanceBetId(null);
								}

								const rewardInSui = fromBaseUnits(currentLootboxEvent?.parsedJson.reward ?? 0);
								const stakeValue = selectedLootbox ? selectedLootbox.price : rewardInSui;
								const multiplier = stakeValue > 0 ? rewardInSui / stakeValue : 0;
								const didWin = selectedLootbox ? rewardInSui > selectedLootbox.price : rewardInSui > 0;
								const winIntensity = didWin ? winIntensityFromMultiplier(multiplier, 20) : 0;
								if (didWin) {
									const { intensity, duration } = calculateConfettiBurst({ rewardValue: rewardInSui, stakeValue, multiplier });
									triggerConfetti({ intensity, duration });
								}
								playResultSound({ isWin: didWin, winSounds, loseSounds, options: { context: 'lootbox', winIntensity } });
								setIsAnimating(false);
							}
						: undefined,
			});
		});
	}

	// Effects
	useEffect(() => {
		const interval = setInterval(() => {
			setCapyExpression((prevExpression) =>
				!isAnimating && (prevExpression == 'bigSmile' || prevExpression == 'happy') ? (Math.random() > 0.5 ? 'bigSmile' : 'happy') : prevExpression,
			);
		}, 8000);

		return () => clearInterval(interval);
	}, [isAnimating]);

	useEffect(() => {
		handleScroll();
	}, [selectedLootbox]);

	useEffect(() => {
		initializeRectangles();
		updateRectangles(lootbox_50_SUI);
		slowRotate();

		return () => {
			gsap.killTweensOf('.rectangle');
			if (slowRotateTween.current) {
				slowRotateTween.current.kill();
			}
		};
	}, []);

	useEffect(() => {
		updateRectangles(lootbox);
		gsap.killTweensOf('.rectangle');
		updateBigRectangleContent();
		if (selectedLootbox) {
			const rectangles = containerRef.current?.querySelectorAll('.rectangle');
			if (rectangles) {
				gsap.fromTo(rectangles, { scale: 0.99 }, { scale: 1, duration: 0.7, ease: 'elastic.out(1, 0.3)' });
			}
		}
		slowRotate();
	}, [lootbox, selectedLootbox]);

	useEffect(() => {
		if (!isAnimating) {
			if (idleResumeTimeout.current) {
				clearTimeout(idleResumeTimeout.current);
				idleResumeTimeout.current = null;
			}

			const shouldDelay = wasAnimatingRef.current;
			wasAnimatingRef.current = false;

			gsap.killTweensOf('.rectangle');
			updateBigRectangleContent();

			const startIdleRotation = () => {
				slowRotate();
			};

			if (shouldDelay) {
				idleResumeTimeout.current = setTimeout(startIdleRotation, 2000);
			} else {
				startIdleRotation();
			}
		} else {
			wasAnimatingRef.current = true;
			if (idleResumeTimeout.current) {
				clearTimeout(idleResumeTimeout.current);
				idleResumeTimeout.current = null;
			}
		}

		return () => {
			if (idleResumeTimeout.current) {
				clearTimeout(idleResumeTimeout.current);
				idleResumeTimeout.current = null;
			}
		};
	}, [isAnimating]);

	useEffect(() => {
		if (currentLootboxEvent) {
			startRotation();
			setIsTransactionPending(false);
		}
	}, [currentLootboxEvent]);

	// Event handlers
	const handleLootboxClick = (lootbox: LootboxType) => {
		if (isAnimating) return;
		setSelectedLootbox(lootbox);
		setLootbox(lootbox);
	};

	const handleOpen = async () => {
		const box = selectedLootbox || lootbox_5_SUI;
		setSelectedLootbox(box);

		const suigarBox = suigar?.lootboxes.find((lb) => lb.title == box.name);
		const stakeBaseUnits = toBaseUnits(box.price);

		try {
			await runStandardGameRound({
				betAmount: box.price,
				runGamePlay: runLootboxGame,
				toBaseUnits,
				currencyType: selectedCurrency.type,
				validators: [
					() => {
						if (selectedCurrency.type !== '0x2::sui::SUI') {
							toast({
								title: 'Currency Not Supported',
								description: 'Lootboxes can only be opened with SUI. Please switch to SUI currency.',
								status: 'warning',
							});
							return false;
						}
						return true;
					},
					() => {
						if (!suigarBox?.id) {
							toast({
								title: 'Lootbox Unavailable',
								description: 'Selected lootbox configuration is missing. Please try again.',
								status: 'error',
							});
							return false;
						}
						return true;
					},
					() => {
						if (displayedBalance === null) {
							return false;
						}
						if (displayedBalance < stakeBaseUnits) {
							openDepositModal(true);
							return false;
						}
						return true;
					},
				],
				createTransaction: () =>
					createPlayTransaction(
						{
							id: suigarBox?.id ?? '',
							price: suigarBox?.price ?? stakeBaseUnits,
						},
						address!,
					),
				parseResponse,
				getBetId: (lootboxEvent) => lootboxEvent.parsedJson.bet_id,
				getRewardBaseUnits: (lootboxEvent) => Number(lootboxEvent.parsedJson.reward ?? 0),
				getDidWin: (lootboxEvent) => Boolean(lootboxEvent.parsedJson.win),
				onBalanceReserved: ({ balanceBetId }) => {
					setCurrentBalanceBetId(balanceBetId);
				},
				onAfterSuccess: ({ parsed }) => {
					setCurrentLootboxEvent(parsed);
				},
				onError: (error) => {
					if (isSignatureInvalidError(error)) {
						disconnect();
						openLoginModal({ sessionExpired: true });
					} else {
						const description = normalizeSuiErrorMessage(error, {
							currencySymbol: currencyStore.symbol,
							fallback: 'An unknown error occurred',
						});
						toast({
							title: 'Transaction Failed',
							description,
							status: 'error',
						});
					}
				},
			});
		} catch {
			// Errors are surfaced via onError.
		}
	};

	const handleBackClick = () => {
		setSelectedLootbox(undefined);
	};

	const onClose = () => setIsOpen(false);

	// Memoized values
	const shownLootboxActivities = useMemo(
		() => filterPendingActivities(lootboxActivities, pendingIdsFor('lootbox')),
		[lootboxActivities, pendingIdsFor],
	);

	return {
		// Data
		lootboxActivities,
		lootboxActivitiesLoading,
		topLootboxActivities,
		topLootboxActivitiesLoading,
		suigar,
		suigarIsLoading,
		shownLootboxActivities,

		// State
		isOpen,
		isAnimating,
		selectedLootbox,
		lootbox,
		topFadeOpacity,
		bottomFadeOpacity,
		capyExpression,
		currentLootboxEvent,
		currentBalanceBetId,
		isTransactionPending,
		// no local pending list; use global pending reveal in filters
		confetti,

		// Refs
		containerRef,
		bigRectangleRef,
		slowRotateTween,
		scrollRef,

		// Functions
		onClose,
		createPlayTransaction,
		parseResponse,
		handleScroll,
		initializeRectangles,
		updateRectangles,
		updateBigRectangleContent,
		slowRotate,
		startRotation,
		handleLootboxClick,
		handleOpen,
		handleBackClick,

		// Utility functions
		selectRandomCandy,
		selectTopThreePrize,
		getRandomExpression,

		// Assets
		lootboxes,
		lootbox_5_SUI,
		lootbox_50_SUI,
	};
}
